import { useRef, useState } from "react";
import { api, BACKEND_URL } from "../lib/api";
import { toast } from "sonner";

/**
 * Upload image button — opens file picker, uploads to backend, returns full URL.
 * onChange(url) called with the public URL after successful upload.
 */
export default function ImageUpload({ value, onChange, label = "Upload", aspect = "aspect-video", className = "" }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);

  const pick = () => ref.current?.click();

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image too large (max 10MB)");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const fullUrl = `${BACKEND_URL}${r.data.url}`;
      onChange?.(fullUrl);
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };

  return (
    <div className={className}>
      <input ref={ref} type="file" accept="image/*" onChange={upload} className="hidden" data-testid={`upload-input-${label.replace(/\s/g, "-").toLowerCase()}`} />
      <div className={`relative ${aspect} bg-stone-100 border border-stone-300 overflow-hidden group cursor-pointer`} onClick={pick}>
        {value ? (
          <img src={value} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-stone-500">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <circle cx="9" cy="9" r="2" />
              <path d="M21 15l-5-5L5 21" />
            </svg>
            <div className="text-xs mt-2 uppercase tracking-[0.2em]">{label}</div>
          </div>
        )}
        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <span className="px-4 py-2 bg-white text-stone-900 text-xs uppercase tracking-[0.2em]" data-testid={`upload-btn-${label.replace(/\s/g, "-").toLowerCase()}`}>
            {busy ? "Uploading..." : value ? "Change" : "Choose photo"}
          </span>
        </div>
      </div>
      {value && (
        <button type="button" onClick={() => onChange?.("")} className="mt-1 text-[10px] uppercase tracking-[0.2em] text-stone-500 hover:text-red-600">Remove</button>
      )}
    </div>
  );
}
